import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { ReportRef } from '@euler/app/flows/report/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { WiredTaskDetailPageContainer } from '@euler/app/flows/task/pages/detail/components/DetailPageContainer';
import { MaintenancePlanItemNotesBottomSheet } from '@euler/app/flows/task/pages/finish-task/components/MaintenancePlanItemNotesBottomSheet';
import { TaskTimeoutInfo } from '@euler/app/flows/task/types';
import { wrapTaskNavigatorScreen } from '@euler/app/flows/task/wrapTaskNavigatorScreen';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { projectMileageAtDate } from '@euler/functions/projectMileageAtDate';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { ShareReportArgs } from '@euler/functions/sharing/types';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  FinishTaskPreparedData,
  PendingIssueConfirmInfo,
  ServiceSubjectItemForFinishTask,
} from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import {
  FinishTaskRequestMaintenancePlanInfo,
  FinishTaskRequestPayload,
} from '@euler/model/FinishTaskRequestPayload';
import {
  CommonTaskStatus,
  InspectionTaskJobStatus,
  MaintenanceServicePlanDetailType,
} from '@euler/model/enum';
import { array2map } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import { SafeHaptics } from '@euler/utils/haptics';
import { usePersistFn } from '@euler/utils/hooks';
import { makeUniqueId } from '@euler/utils/id';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import isBefore from 'date-fns/isBefore';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTheme } from 'styled-components';
import { MaintenancePlanBottomSheet } from './components/MaintenancePlanBottomSheet';
import { MaintenancePlanItemListPanel } from './components/MaintenancePlanItemListPanel';
import {
  INSURANCE_EXPIRY_ITEM_NAME,
  MaintenancePlanListItem,
  MaintenancePlanListItemPlanInfo,
  MaintenancePlanListItemType,
} from './types';
import {
  isFaultLikeSubjectMaintenancePlanItem,
  isPendingIssueMaintenancePlanItem,
  maintenancePlanListItemInfoFromItem,
} from './utils';

export type ServiceSubjectItem = ServiceSubjectItemForFinishTask;

// const kTestVin = 'L6T7742D5HN436554';
// const kTestMileage = 3333;

export const FinishTaskPage = wrapTaskNavigatorScreen(
  ({
    timeoutInfo,
    preparedData,
  }: {
    timeoutInfo?: TaskTimeoutInfo;
    preparedData: FinishTaskPreparedData;
  }) => {
    const isMobile = useIsMobileLayout();
    const navigation = useTaskNavigation();
    const confirm = useConfirm();
    const appLoading = useAppLoading();
    const alert = useAlert();
    const theme = useTheme();

    const { rpx } = useResponsiveContext();
    const { event$ } = useOrderContext();

    const {
      detail,
      reportTarget,
      taskManager,
      storeConfiguration,
      profile: { store },
    } = useTaskContext();

    const { shareReportToCustomer } = useShareReport();

    const { vin, vehicleMileage: currentMileage } = detail.basicInfo;

    const age = useMemo(() => getVehicleAgeByVin(vin), [vin]);

    const trackAppReview = useAppReviewTracking();
    const viewReport = useViewReportWithTaskContext();

    const [activeItem, setActiveItem] = useState<MaintenancePlanListItem>();
    const [activeItemForNote, setActiveItemForNote] =
      useState<MaintenancePlanListItem>();
    const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
    const [errorShakeNonce, setErrorShakeNonce] = useState(0);

    const clearItemError = useCallback((id: string) => {
      setItemErrors(prev => {
        if (prev[id] == null) return prev;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _id, ...rest } = prev;
        return rest;
      });
    }, []);

    const viewItemDetail = useCallback(
      (item: MaintenancePlanListItem) => {
        const reportRef: ReportRef = {
          type: 'report',
          orderNo: detail.basicInfo.orderNo,
          reportNo: detail.basicInfo.reportNo,
          id: detail.basicInfo.reportNo,
          licensePlateNo: detail.basicInfo.licensePlateNo,
          vehicleName: detail.basicInfo.vehicleName,
          vehicleImageUrl: detail.basicInfo.vehicleImageUrl,
        };

        if (item.type === 'inspection-subject') {
          viewReport('inspectionReport', {
            reportRef,
            flowId: item.flowId,
            title: item.name,
            canEdit: () => false,
          });
        } else if (item.type === 'construction-job') {
          viewReport('constructionReport', {
            reportRef,
            title: item.name,
            targetJobId: item.jobId,
            canEdit: () => false,
            canFinish: () => false,
            canShare: () => false,
          });
        } else if (item.type === 'pending-issue') {
          navigation.push('_pendingIssueWithDetails', {
            pendingIssueWithDetails: item.issue,
          });
        }
      },
      [
        detail.basicInfo.licensePlateNo,
        detail.basicInfo.orderNo,
        detail.basicInfo.reportNo,
        detail.basicInfo.vehicleImageUrl,
        detail.basicInfo.vehicleName,
        navigation,
        viewReport,
      ],
    );

    const [listItems, setListItems] = useState<MaintenancePlanListItem[]>(
      () => {
        const list: MaintenancePlanListItem[] = [];
        const existingPlanDetailMap = array2map(
          preparedData.maintenancePlanItems,
          x => x.name,
        );

        for (const subject of preparedData.subjects) {
          const existingPlanDetail = existingPlanDetailMap.get(subject.name);
          const planInfo: MaintenancePlanListItemPlanInfo = {
            remark: existingPlanDetail?.remark,
            nextServiceDate: existingPlanDetail?.nextServiceDate,
            nextServiceMileage: existingPlanDetail?.nextServiceMileage,
            estimatedNextServiceMileage: existingPlanDetail?.nextServiceDate
              ? projectMileageAtDate(
                  currentMileage,
                  age,
                  existingPlanDetail.nextServiceDate,
                )
              : undefined,
          };
          if (subject.type === 'inspection') {
            list.push({
              id: makeUniqueId(),
              type: 'inspection-subject',
              name: subject.name,
              author: subject.author,
              flowId: subject.flowId,
              iconUrl: subject.iconUrl,
              planInfo,
            });
          } else if (subject.type === 'construction-job') {
            list.push({
              id: makeUniqueId(),
              type: 'construction-job',
              name: subject.name,
              jobId: subject.jobId,
              author: subject.author ?? '',
              planInfo,
            });
          } else if (subject.type === 'pending-issue') {
            list.push({
              id: makeUniqueId(),
              type: 'pending-issue',
              name: subject.name,
              issue: subject.issue,
              author: subject.author,
              repairConfirmed: true,
              unsolvedReason: subject.unsolvedReason,
              remark: subject.remark,
              planInfo,
              customerSignatureImgUrl:
                existingPlanDetail?.customerSignatureImgUrl,
              customerSignatureUnavailableReason:
                existingPlanDetail?.customerSignatureUnavailableReason,
              customerSignatureUnavailableRemark:
                existingPlanDetail?.customerSignatureUnavailableRemark,
            });
          } else if (subject.type === 'custom') {
            list.push({
              id: makeUniqueId(),
              type: 'custom',
              name: subject.name,
              source: subject.source,
              removable: subject.removable,
              requiresNextServiceDate: subject.requiresNextServiceDate,
              label: subject.label,
              planInfo,
            });
          }
        }

        for (const pendingIssue of preparedData.pendingIssues) {
          if (
            !preparedData.subjects.find(x => x.name === pendingIssue.issue.name)
          ) {
            const confirmation = preparedData.confirmedPendingIssues.find(
              x => x.name === pendingIssue.issue.name,
            );
            const existingPlanDetail = existingPlanDetailMap.get(
              pendingIssue.issue.name,
            );
            const planInfo: MaintenancePlanListItemPlanInfo = {
              remark: existingPlanDetail?.remark,
              nextServiceDate: existingPlanDetail?.nextServiceDate,
              nextServiceMileage: existingPlanDetail?.nextServiceMileage,
              estimatedNextServiceMileage: existingPlanDetail?.nextServiceDate
                ? projectMileageAtDate(
                    currentMileage,
                    age,
                    existingPlanDetail.nextServiceDate,
                  )
                : undefined,
            };
            list.push({
              id: makeUniqueId(),
              type: 'pending-issue',
              name: pendingIssue.issue.name,
              issue: pendingIssue,
              author:
                pendingIssue.details
                  .flatMap(x => x.authorName)
                  .filter(Boolean)[0] ?? undefined,
              repairConfirmed: false,
              unsolvedReason:
                confirmation?.unsolvedReason ??
                pendingIssue.issue.unsolvedReason ??
                undefined,
              remark:
                confirmation?.remark ?? pendingIssue.issue.remark ?? undefined,
              planInfo,
              customerSignatureImgUrl:
                existingPlanDetail?.customerSignatureImgUrl,
              customerSignatureUnavailableReason:
                existingPlanDetail?.customerSignatureUnavailableReason,
              customerSignatureUnavailableRemark:
                existingPlanDetail?.customerSignatureUnavailableRemark,
            });
          }
        }

        const faultLikeSubjects =
          detail.basicInfo.orderDetail?.serviceSubjects?.filter(
            isFaultLikeServiceSubject,
          );
        if (faultLikeSubjects?.length) {
          for (const faultLikeSubject of faultLikeSubjects) {
            const subject = detail.basicInfo.orderDetail?.serviceSubjects
              ?.filter(isFaultLikeServiceSubject)
              .find(
                x =>
                  x.type === faultLikeSubject.type &&
                  x.name === faultLikeSubject.name,
              );
            if (subject == null) {
              console.warn(
                `Cannot find fault like service subject info for ${faultLikeSubject.name}`,
              );
              continue;
            }
            const confirmation = preparedData.confirmedFaultLikeSubjects.find(
              x => x.name === faultLikeSubject.name,
            );
            const item = preparedData.maintenancePlanItems.find(
              x =>
                x.type === MaintenanceServicePlanDetailType.FaultLikeSubject &&
                x.name === faultLikeSubject.name,
            );
            const faultInspectionItem: FaultInspectionItem = {
              type: subject.type,
              title: faultLikeSubject.name,
              relatedInspections: subject.relatedInspections,
              relatedJobs: subject.relatedJobs?.map(relatedJob => ({
                ...relatedJob,
                jobStatus: InspectionTaskJobStatus.Finished,
              })),
              remark: subject.remark,
              unsolvedReason: subject.unsolvedReason,
              note: subject.note,
              assignedTo: subject.assignedTo,
              status:
                subject.relatedJobs?.length &&
                detail.construction.status === CommonTaskStatus.Finished
                  ? 'solved'
                  : subject.status,
            };
            list.push({
              id: makeUniqueId(),
              type: 'fault',
              name: faultLikeSubject.name,
              solved:
                confirmation?.solved || faultInspectionItem.status === 'solved',
              unsolvedReason: confirmation?.unsolvedReason,
              remark: confirmation?.remark,
              faultInspectionItem,
              planInfo: item
                ? {
                    remark: item.remark,
                    nextServiceDate: item.nextServiceDate,
                    nextServiceMileage: item.nextServiceMileage,
                    estimatedNextServiceMileage: projectMileageAtDate(
                      currentMileage,
                      age,
                      item.nextServiceDate,
                    ),
                  }
                : undefined,
            });
          }
        }

        for (const item of preparedData.maintenancePlanItems) {
          if (item.type === MaintenanceServicePlanDetailType.Custom) {
            if (list.some(x => x.type === 'custom' && x.name === item.name)) {
              continue;
            }
            list.push({
              id: makeUniqueId(),
              type: 'custom',
              name: item.name,
              planInfo: {
                remark: item.remark,
                nextServiceDate: item.nextServiceDate,
                nextServiceMileage: item.nextServiceMileage,
                estimatedNextServiceMileage: projectMileageAtDate(
                  currentMileage,
                  age,
                  item.nextServiceDate,
                ),
              },
            });
          }
        }

        const insuranceSubject = preparedData.subjects.find(
          x => x.type === 'insurance-expiry',
        );
        if (insuranceSubject?.type === 'insurance-expiry') {
          const existingInsuranceItem = preparedData.maintenancePlanItems.find(
            x => x.type === MaintenanceServicePlanDetailType.InsuranceExpiry,
          );
          const optedIn =
            existingInsuranceItem != null ||
            preparedData.maintenancePlanItems.length === 0;
          list.push({
            id: makeUniqueId(),
            type: 'insurance-expiry',
            name: INSURANCE_EXPIRY_ITEM_NAME,
            optedIn,
            removable: insuranceSubject.removable,
            planInfo: {
              remark: existingInsuranceItem?.remark,
              nextServiceDate: insuranceSubject.expiryDate,
              nextServiceMileage: undefined,
              estimatedNextServiceMileage: undefined,
            },
          });
        }

        return list;
      },
    );

    const onItemActionPress = useCallback(
      (item: MaintenancePlanListItem) => {
        const { action } = maintenancePlanListItemInfoFromItem(item);

        if (action.actionType !== 'view-detail') {
          clearItemError(item.id);
        }

        switch (action.actionType) {
          case 'view-detail': {
            viewItemDetail(item);
            break;
          }
          case 'confirm-repair': {
            setListItems(items =>
              items.map(x =>
                x.type === 'pending-issue' &&
                item.type === x.type &&
                x.name === item.name
                  ? { ...x, repairConfirmed: true }
                  : x,
              ),
            );
            break;
          }
          case 'reset-as-pending-issue': {
            setListItems(items =>
              items.map(x =>
                x.type === 'pending-issue' &&
                item.type === x.type &&
                x.name === item.name
                  ? { ...x, repairConfirmed: false }
                  : x,
              ),
            );
            break;
          }
          case 'remove': {
            setListItems(items =>
              items.filter(x => x.type !== 'custom' || x.name !== item.name),
            );
            break;
          }
          case 'confirm-solve-fault-like-subject':
            setListItems(items =>
              items.map(x =>
                x.type === 'fault' &&
                item.type === x.type &&
                x.name === item.name
                  ? { ...x, solved: true }
                  : x,
              ),
            );
            break;
          case 'reset-fault-like-subject-unsolved':
            setListItems(items =>
              items.map(x =>
                x.type === 'fault' &&
                item.type === x.type &&
                x.name === item.name
                  ? { ...x, solved: false }
                  : x,
              ),
            );
            break;
          case 'opt-in-insurance-expiry':
            setListItems(items =>
              items.map(x =>
                x.type === 'insurance-expiry' ? { ...x, optedIn: true } : x,
              ),
            );
            break;
          case 'opt-out-insurance-expiry':
            setListItems(items =>
              items.map(x =>
                x.type === 'insurance-expiry' ? { ...x, optedIn: false } : x,
              ),
            );
            break;
        }
      },
      [viewItemDetail, clearItemError],
    );

    const onSuccess = usePersistFn(() => {
      navigation.replace('_hooray', {
        type: 'success',
        soundType: 'chirp',
        buttonText: '完成',
        onPress: () => {
          event$.next({
            type: 'finished',
            orderNo: detail.basicInfo.orderNo,
            taskNo: detail.basicInfo.taskNo,
            isMainOrder: detail.basicInfo.isMainOrder,
          });
          navigation.navigate('Main', {
            screen: 'Tabs',
            params: { screen: 'History' },
          } as any);
          trackAppReview();
        },
        onShare: async () => {
          const args: ShareReportArgs = {
            target: { type: reportTarget.type, id: reportTarget.id },
            licensePlateNo: reportTarget.licensePlateNo,
            orderNo: reportTarget.orderNo,
            reportNo: reportTarget.reportNo,
            tab: 'deliveryCheck',
          };
          await shareReportToCustomer(args);
        },
      });
    });

    const onFinish = usePersistFn(async () => {
      if (
        storeConfiguration.checkOrderTimeoutBeforeFinish &&
        detail.basicInfo.estimatedFinishTime != null &&
        isBefore(
          dateFromValue(detail.basicInfo.estimatedFinishTime),
          new Date(),
        ) &&
        !timeoutInfo
      ) {
        void alert({ title: '请填写超时原因!' });
        return;
      }

      const errors: Record<string, string> = {};
      for (const item of listItems) {
        if (
          item.type === 'custom' &&
          item.requiresNextServiceDate &&
          item.planInfo?.nextServiceDate == null
        ) {
          errors[item.id] = `请设置${item.label || '下次保养日期'}`;
          continue;
        }
        if (item.type === 'pending-issue' && !item.repairConfirmed) {
          if (
            storeConfiguration.requiresUnsolvedReasonForPendingIssue &&
            !item.unsolvedReason
          ) {
            errors[item.id] = '请填写遗留原因';
            continue;
          }
          if (
            storeConfiguration.requiresNextServiceDateForPendingIssue &&
            item.planInfo?.nextServiceDate == null
          ) {
            errors[item.id] = '请设置下次保养日期';
            continue;
          }
          if (
            storeConfiguration.requiresCustomerSignForPendingIssue &&
            !item.customerSignatureImgUrl &&
            !item.customerSignatureUnavailableReason
          ) {
            errors[item.id] = '请完成客户签字确认';
            continue;
          }
        }
      }
      if (Object.keys(errors).length > 0) {
        setItemErrors(errors);
        setErrorShakeNonce(n => n + 1);
        SafeHaptics.notificationError();
        return;
      }
      setItemErrors({});

      const confirmed = await confirm({
        title: '您即将完工交车并推送报告给车主，是否继续?',
      });

      if (!confirmed) return;

      await appLoading.showWithAction(async () => {
        const payload: FinishTaskRequestPayload = {
          autoCleanupMaintenancePlans: true,
          timeoutInfo: timeoutInfo
            ? {
                timeoutReason: timeoutInfo.reason,
                timeoutRemarks: timeoutInfo.remark,
              }
            : undefined,
          pendingIssueConfirmations: listItems
            .filter(isPendingIssueMaintenancePlanItem)
            .map<PendingIssueConfirmInfo>(x => ({
              name: x.name,
              solved: x.repairConfirmed,
              unsolvedReason: !x.repairConfirmed ? x.unsolvedReason : undefined,
              remark: x.remark,
            })),
          faultConfirmations: listItems
            .filter(isFaultLikeSubjectMaintenancePlanItem)
            .map(x => ({
              name: x.name,
              type: x.faultInspectionItem.type,
              solved: x.solved ?? false,
              unsolvedReason: !x.solved ? x.unsolvedReason : undefined,
              remark: x.remark,
            })),
          maintenancePlans: listItems
            .filter(
              x =>
                x.planInfo?.nextServiceDate != null ||
                (x.type === 'pending-issue' &&
                  !x.repairConfirmed &&
                  (x.customerSignatureImgUrl != null ||
                    x.customerSignatureUnavailableReason != null)),
            )
            .filter(x => x.type !== 'insurance-expiry' || x.optedIn)
            .map<FinishTaskRequestMaintenancePlanInfo>(x => ({
              name: x.type === 'insurance-expiry' ? '保险到期' : x.name,
              type: maintenancePlanListItemInfoFromItem(x).planDetailType,
              source: x.type === 'custom' ? x.source : undefined,
              nextServiceDate: x.planInfo?.nextServiceDate,
              nextServiceMileage:
                x.type === 'insurance-expiry'
                  ? undefined
                  : x.planInfo?.nextServiceMileage ??
                    x.planInfo?.estimatedNextServiceMileage,
              remark: x.planInfo?.remark,
              customerSignatureImgUrl:
                x.type === 'pending-issue'
                  ? x.customerSignatureImgUrl
                  : undefined,
              customerSignatureUnavailableReason:
                x.type === 'pending-issue'
                  ? x.customerSignatureUnavailableReason
                  : undefined,
              customerSignatureUnavailableRemark:
                x.type === 'pending-issue'
                  ? x.customerSignatureUnavailableRemark
                  : undefined,
            })),
        };
        if (!store.isIndependentDeliveryCheckEnabled) {
          payload.finishDeliveryCheckWhenNecessary = true;
        }
        await taskManager.finish(payload);
        onSuccess();
      });
    });

    const onAddCustomMaintenancePlanItem = useCallback(() => {
      setActiveItem({ id: makeUniqueId(), type: 'custom', name: '' });
    }, []);

    const onEditPlanInfo = useCallback((item: MaintenancePlanListItem) => {
      setActiveItem(item);
    }, []);

    const onCancelEditPlanInfo = useCallback(() => {
      setActiveItem(undefined);
    }, []);

    const onDoneEditPlanInfo = useCallback(
      (values: {
        id: string;
        name: string;
        type: MaintenancePlanListItemType;
        nextServiceDate: string | Date;
        nextServiceMileage: number | undefined;
        remark?: string;
      }) => {
        let isCustomNameExists = false;
        setListItems(items => {
          // try to get the existing item
          const index = items.findIndex(x => x.id === values.id);
          if (index >= 0) {
            return items.map<MaintenancePlanListItem>((item, i) =>
              i === index
                ? {
                    ...item,
                    name: item.type === 'custom' ? values.name : item.name,
                    planInfo: {
                      remark: values.remark,
                      nextServiceDate: values.nextServiceDate,
                      nextServiceMileage: values.nextServiceMileage,
                      estimatedNextServiceMileage: projectMileageAtDate(
                        currentMileage,
                        age,
                        values.nextServiceDate,
                      ),
                    },
                  }
                : item,
            );
          }

          if (values.type === 'custom') {
            // create a new custom maintenance plan item
            if (items.some(x => x.name === values.name)) {
              isCustomNameExists = true;
              return items;
            }
            const newItem: MaintenancePlanListItem = {
              id: values.id,
              type: 'custom',
              name: values.name,
              planInfo: {
                remark: values.remark,
                nextServiceDate: values.nextServiceDate,
                nextServiceMileage: values.nextServiceMileage,
                estimatedNextServiceMileage: projectMileageAtDate(
                  currentMileage,
                  age,
                  values.nextServiceDate,
                ),
              },
            };
            return [...items, newItem];
          }

          return items;
        });

        if (isCustomNameExists) {
          throw new Error('已存在同名的项目，请修改名称后重试!');
        }

        clearItemError(values.id);
        setActiveItem(undefined);
      },
      [age, currentMileage, clearItemError],
    );

    const onEditNote = useCallback((item: MaintenancePlanListItem) => {
      setActiveItemForNote(item);
    }, []);

    const onCancelEditNote = useCallback(() => {
      setActiveItemForNote(undefined);
    }, []);

    const onDoneEditNote = useCallback(
      (values: {
        id: string;
        type: MaintenancePlanListItemType;
        unsolvedReason?: string;
        remark?: string;
        customerSignatureImgUrl?: string;
        customerSignatureUnavailableReason?: string;
        customerSignatureUnavailableRemark?: string;
      }) => {
        setListItems(items => {
          // try to get the existing item
          const index = items.findIndex(x => x.id === values.id);
          if (index >= 0) {
            return items.map((item, i) => {
              if (i !== index) return item;
              if (item.type === 'pending-issue') {
                return {
                  ...item,
                  unsolvedReason: values.unsolvedReason,
                  remark: values.remark,
                  customerSignatureImgUrl: values.customerSignatureImgUrl,
                  customerSignatureUnavailableReason:
                    values.customerSignatureUnavailableReason,
                  customerSignatureUnavailableRemark:
                    values.customerSignatureUnavailableRemark,
                };
              }
              if (item.type === 'fault') {
                return {
                  ...item,
                  unsolvedReason: values.unsolvedReason,
                  remark: values.remark,
                };
              }
              return item;
            });
          }

          return items;
        });

        clearItemError(values.id);
        setActiveItemForNote(undefined);
      },
      [clearItemError],
    );

    const onViewFaultItemInspectionDetails = usePersistFn(
      async (item: FaultInspectionItem) => {
        viewReport('inspectionReport', {
          title: `${item.title}-检测结果`,
          faultLikeSubject: item,
          canFinish: () => false,
          canShare: () => false,
          canEdit: () => false,
        });
      },
    );

    const onViewFaultItemConstructionDetails = usePersistFn(
      async (item: FaultInspectionItem) => {
        viewReport('constructionReport', {
          title: `${item.title}-施工反馈`,
          faultLikeSubject: item,
          canFinish: () => false,
          canShare: () => false,
          canEdit: () => false,
        });
      },
    );

    const commonItemListProps = {
      itemErrors,
      errorShakeNonce,
      onItemActionPress,
      onViewItemDetail: viewItemDetail,
      onEditItemPlanInfo: onEditPlanInfo,
      onEditItemNote: onEditNote,
      onViewFaultItemInspectionDetails: onViewFaultItemInspectionDetails,
      onViewFaultItemConstructionDetails: onViewFaultItemConstructionDetails,
    };

    return (
      <>
        <WiredTaskDetailPageContainer
          title="完工交车"
          showVehicleInfoForIpadNavHeader={isMobile}
          scrollViewForTablet={true}
          titleStyleForIpadNavHeader={{
            fontFamily: FontFamily.NotoSans.Bold,
            fontWeight: '600',
          }}
        >
          <MaintenancePlanItemListPanel
            title="本次服务项目"
            emptyText="本次服务暂无相关服务项目"
            items={listItems.filter(
              x =>
                x.type === 'inspection-subject' ||
                x.type === 'construction-job' ||
                (x.type === 'pending-issue' && x.repairConfirmed),
            )}
            {...commonItemListProps}
          />

          <MaintenancePlanItemListPanel
            title="遗留项目及商机管理"
            emptyText="本次服务暂无相关遗留项目"
            countLabelColor={StatusColors.Warn}
            items={listItems.filter(
              x => x.type === 'pending-issue' && !x.repairConfirmed,
            )}
            {...commonItemListProps}
          />

          <MaintenancePlanItemListPanel
            title="故障检修服务确认"
            emptyText={null}
            countLabelColor={StatusColors.Info}
            items={listItems.filter(x => x.type === 'fault')}
            {...commonItemListProps}
          />

          <MaintenancePlanItemListPanel
            title="保险到期提醒"
            emptyText={null}
            countLabelColor={StatusColors.Info}
            items={listItems.filter(x => x.type === 'insurance-expiry')}
            {...commonItemListProps}
          />

          <MaintenancePlanItemListPanel
            title="自定义提醒计划"
            emptyText="暂未添加自定义保养服务提醒计划"
            showEmptyIcon={false}
            emptyTextStyle={{ textAlign: 'left', fontSize: rpx(12) }}
            items={listItems.filter(x => x.type === 'custom')}
            {...commonItemListProps}
            emptyViewStyle={{ alignItems: 'flex-start' }}
            titleRight={
              <LinkButton
                text="添加计划"
                textStyle={{ fontSize: rpx(12), marginLeft: 0 }}
                onPress={onAddCustomMaintenancePlanItem}
                icon={
                  <Icon name="plus-circle" size={rpx(14)} color={theme.link} />
                }
              />
            }
          />

          <View
            css={`
              height: ${44}px;
            `}
          />
        </WiredTaskDetailPageContainer>
        <BottomAction>
          <BottomAction.Button
            text="确认完工交车"
            variant={'primary'}
            onPress={onFinish}
          />
        </BottomAction>
        <MaintenancePlanBottomSheet
          show={activeItem != null}
          target={activeItem}
          defaultNextServiceDate={activeItem?.planInfo?.nextServiceDate}
          defaultNextServiceMileage={activeItem?.planInfo?.nextServiceMileage}
          onClose={onCancelEditPlanInfo}
          onDone={onDoneEditPlanInfo}
        />
        <MaintenancePlanItemNotesBottomSheet
          show={activeItemForNote != null}
          target={activeItemForNote}
          onClose={onCancelEditNote}
          onDone={onDoneEditNote}
        />
      </>
    );
  },
);
